import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { getProducts, getProductCategories, ProductDetail } from "@/services/productService";

export default function ProductsPage() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [products, setProducts] = useState<ProductDetail[]>([]);
  const [categories, setCategories] = useState<{ name: string; slug: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        const [prods, cats] = await Promise.all([
          getProducts(),
          getProductCategories(),
        ]);
        setProducts(prods);
        setCategories([{ name: "All", slug: "all" }, ...cats.map((c) => ({ name: c.name, slug: c.slug }))]);
        setError(null);
      } catch (err) {
        setError("Failed to load products. Please try again.");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const filtered = activeCategory === "all"
    ? products
    : products.filter((p) => p.category.toLowerCase().replace(/\s+/g, "-") === activeCategory);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Schema JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Fire Safety Products Singapore",
            description: "Complete range of fire safety products and equipment in Singapore",
            itemListElement: products.map((p, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: p.title,
              url: `/products/${p.slug}`,
            })),
          }),
        }}
      />

      <main>
        {/* Hero */}
        <section className="relative pt-24 pb-14 md:pt-32 md:pb-18 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://storage.readdy-site.link/project_files/bad53886-d0ad-4ee3-acb4-5b763a394fe2/7a6e0fb7-172b-415e-b920-c65ed72e2215_compressed_c83f4e5a-e094-462d-9fcc-f560f079a53d.webp"
              alt="Emergency lighting equipment installation by Ezzogenics"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/70 via-foreground-950/60 to-foreground-950/80"></div>
          </div>
          <div className="relative z-10 px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-400 border border-accent-400/40 rounded-full px-3 py-1 mb-4">
              Fire Safety Equipment
            </span>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 leading-tight mb-4 max-w-3xl">
              Fire Safety <span className="text-primary-400">Products</span> Singapore
            </h1>
            <p className="text-background-200 text-base md:text-lg max-w-2xl leading-relaxed mb-6">
              Industry-standard fire safety equipment for residential, commercial, and industrial premises across Singapore. SCDF-compliant products from leading manufacturers.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              Request a Quote
              <i className="ri-arrow-right-line text-base"></i>
            </Link>
          </div>
        </section>

        {/* Category Filter */}
        <section className="sticky top-[64px] z-30 bg-background-50/95 backdrop-blur-md border-b border-background-200/70 py-3 px-4 md:px-6 lg:px-10">
          <div className="max-w-7xl mx-auto flex items-center gap-2 overflow-x-auto scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat.slug}
                onClick={() => setActiveCategory(cat.slug)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  activeCategory === cat.slug
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </section>

        {/* Products Grid */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16">
          <div className="max-w-7xl mx-auto">
            {loading && (
              <div className="flex items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-primary-300 border-t-primary-500 rounded-full animate-spin"></div>
              </div>
            )}

            {error && (
              <div className="text-center py-16">
                <p className="text-foreground-600 mb-4">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors"
                >
                  Retry
                </button>
              </div>
            )}

            {!loading && !error && (
              <>
                <p className="text-foreground-500 text-sm mb-8">
                  Showing <strong className="text-foreground-900">{filtered.length}</strong> products
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6" data-product-shop>
                  {filtered.map((product) => (
                    <Link
                      key={product.id}
                      to={`/products/${product.slug}`}
                      className="group bg-background-50 rounded-2xl border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-300 block"
                    >
                      {/* Image */}
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={product.cardImage}
                          alt={`${product.title} Singapore`}
                          title={`${product.title} - Ezzogenics`}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                        <div className="absolute top-3 left-3">
                          <span className="inline-block text-xs font-semibold bg-background-50/90 text-foreground-700 rounded-full px-2.5 py-1">
                            {product.category}
                          </span>
                        </div>
                      </div>

                      {/* Content */}
                      <div className="p-5">
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-9 h-9 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                            <i className={`${product.icon} text-primary-600 text-base`}></i>
                          </div>
                          <h2 className="font-heading text-base font-bold text-foreground-950 leading-snug group-hover:text-primary-600 transition-colors">
                            {product.title}
                          </h2>
                        </div>
                        <p className="text-foreground-600 text-sm leading-relaxed line-clamp-3 mb-4">
                          {product.excerpt}
                        </p>
                        <div className="flex items-center gap-1 text-primary-500 text-sm font-semibold">
                          Learn more
                          <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform"></i>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        </section>

        {/* CTA Banner */}
        <section className="bg-background-100 border-t border-background-200/70 py-12 md:py-16">
          <div className="px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="bg-primary-500 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-2">
                  Not sure which product you need?
                </h2>
                <p className="text-background-100 text-sm md:text-base max-w-xl">
                  Our SCDF-certified fire safety consultants will assess your premises and recommend the right systems for your building and budget.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-background-50 text-primary-600 text-sm font-semibold hover:bg-background-100 transition-colors whitespace-nowrap"
                >
                  Get Free Consultation
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}